/* ==========================================================
   HELPERS GENERALES — VERSIÓN OPTIMIZADA
   ==========================================================
   Archivo: section-product-utils.js
   
   ✅ Misma API — las funciones se llaman EXACTAMENTE igual
   ✅ Compatible con todas las secciones existentes
   
   OPTIMIZACIONES:
   
   1. initGallerySlider → IntersectionObserver
      Antes: inicializaba TODAS las galerías de golpe
      Ahora: cada galería se inicia solo cuando es visible
   
   2. initVariantSystem → delegación de eventos
      Antes: 1 addEventListener por cada botón de variante
      Ahora: 1 solo listener delegado en el root
   
   3. initAddToCart → delegación de eventos
      Antes: 1 addEventListener por cada botón .btn-add-cart
      Ahora: 1 solo listener delegado en el root
   
   4. Guards contra doble inicialización en todo
   ========================================================== */

/* ==========================================================
   HELPERS DE BOTONES (sin cambios en API)
========================================================== */
function enableBtn(btn) {
  if (!btn) return;
  btn.disabled = false;
  btn.classList.remove("disabled-btn");
}

function disableBtn(btn) {
  if (!btn) return;
  btn.disabled = true;
  btn.classList.add("disabled-btn");
}

function showLoader(btn) {
  if (!btn) return;
  if (!btn.dataset.originalText) {
    btn.dataset.originalText = btn.textContent;
  }
  btn.classList.add("loading");
  btn.innerHTML = '<div class="loader-mini"></div>';
}

function showSuccess(btn, text) {
  if (!btn) return;
  text = text || "AGREGADO";
  btn.innerHTML = text;
  btn.classList.remove("loading");
  setTimeout(function () {
    btn.innerHTML = btn.dataset.originalText || "AGREGAR AL CARRITO";
  }, 1500);
}

/* ==========================================================
   EVENTO CARRITO LATERAL (sin cambios)
========================================================== */
function dispatchHorizonCartUpdate(productId, variantId) {
  document.dispatchEvent(
    new CustomEvent("cart:update", {
      bubbles: true,
      detail: {
        resource: {},
        sourceId: "modular-system",
        data: {
          source: "modular-system",
          productId: productId ? String(productId) : undefined,
          variantId: String(variantId),
          itemCount: 1
        }
      }
    })
  );
}

/* ==========================================================
   SLIDER GALERÍA — CON INTERSECTION OBSERVER
   ==========================================================
   API sin cambios: initGallerySlider(root)
========================================================== */
function initSingleGallery(gal) {
  if (gal.dataset.inited) return;
  gal.dataset.inited = "true";

  new Swiper(gal, {
    slidesPerView: 1,
    loop: gal.querySelectorAll(".swiper-slide").length > 1,
    spaceBetween: 15,
    nested: true,
    pagination: {
      el: gal.querySelector(".swiper-pagination"),
      clickable: true
    },
    navigation: {
      nextEl: gal.querySelector(".gallery-next"),
      prevEl: gal.querySelector(".gallery-prev")
    }
  });
}

function initGallerySlider(root) {
  var galleries = root.querySelectorAll(":scope .product-gallery");
  if (!galleries.length) return;

  if ("IntersectionObserver" in window) {
    var observer = new IntersectionObserver(
      function (entries, obs) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          obs.unobserve(entries[i].target);
          initSingleGallery(entries[i].target);
        }
      },
      { rootMargin: "200px", threshold: 0 }
    );

    galleries.forEach(function (gal) {
      if (gal.dataset.inited) return;

      // Si la galería ya está en el viewport, iniciarla de inmediato
      var rect = gal.getBoundingClientRect();
      var inViewport =
        rect.top < window.innerHeight + 200 && rect.bottom > -200;

      if (inViewport) {
        initSingleGallery(gal);
      } else {
        observer.observe(gal);
      }
    });
  } else {
    galleries.forEach(initSingleGallery);
  }
}

/* ==========================================================
   SISTEMA DE VARIANTES — CON DELEGACIÓN
   ==========================================================
   API sin cambios: initVariantSystem(root)
========================================================== */
function initVariantSystem(root) {
  /* --- Delegación: 1 listener por root --- */
  if (!root.dataset.variantDelegated) {
    root.dataset.variantDelegated = "true";

    root.addEventListener("click", function (e) {
      var btn = e.target.closest(".variante-con-stock");
      if (!btn) return;

      var group = btn.closest(".variant-buttons");
      if (!group) return;

      var slide = group.closest(
        ".swiper-slide, .influencer-popup__product-card"
      );
      var addBtn = slide ? slide.querySelector(".btn-add-cart") : null;
      if (!addBtn) return;

      // Desactivar todos, activar clickeado
      group.querySelectorAll(".variante-con-stock").forEach(function (b) {
        b.classList.remove("active");
      });
      btn.classList.add("active");

      addBtn.dataset.variantId = btn.dataset.variantId;
      enableBtn(addBtn);
    });
  }

  /* --- Habilitar botones con variante ya asignada --- */
  root
    .querySelectorAll(":scope .btn-add-cart[data-variant-id]")
    .forEach(enableBtn);

  /* --- Inputs ocultos (unitalla) --- */
  root
    .querySelectorAll(":scope input.variant-select")
    .forEach(function (input) {
      var slide = input.closest(
        ".swiper-slide, .influencer-popup__product-card"
      );
      var btn = slide ? slide.querySelector(".btn-add-cart") : null;
      if (btn) {
        btn.dataset.variantId = input.value;
        enableBtn(btn);
      }
    });
}

/* ==========================================================
   ADD TO CART — CON DELEGACIÓN
   ==========================================================
   API sin cambios: initAddToCart(root)
========================================================== */
function initAddToCart(root) {
  if (root.dataset.cartDelegated) return;
  root.dataset.cartDelegated = "true";

  root.addEventListener("click", function (e) {
    var btn = e.target.closest(".btn-add-cart");
    if (!btn || btn.disabled) return;

    var variantId = btn.dataset.variantId;

    // Caso POPUP (select de variantes)
    if (!variantId) {
      var card = btn.closest(
        ".influencer-popup__product-card, .swiper-slide"
      );
      var selectPopup = card
        ? card.querySelector(".influencer-popup__variant-select")
        : null;
      if (selectPopup) variantId = selectPopup.value;
    }

    // Caso fallback: input hidden
    if (!variantId) {
      var holder = btn.closest(
        ".swiper-slide, .influencer-popup__product-card"
      );
      var hiddenInput = holder
        ? holder.querySelector(".variant-select")
        : null;
      if (hiddenInput) variantId = hiddenInput.value;
    }

    if (!variantId) {
      alert("Selecciona una variante");
      return;
    }

    showLoader(btn);

    fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ id: Number(variantId), quantity: 1 })
    })
      .then(function (r) {
        return r.json();
      })
      .then(function () {
        showSuccess(btn);
        dispatchHorizonCartUpdate(null, variantId);
      })
      .catch(function (err) {
        console.error(err);
        btn.innerHTML = "Error";
        setTimeout(function () {
          btn.innerHTML = btn.dataset.originalText || "AGREGAR AL CARRITO";
        }, 2000);
      });
  });
}